import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';
import { useCountries } from '../context/CountryContext';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

export default function OpeningsPage() {
  const { countries } = useCountries();
  const [country, setCountry] = useState('All Countries');
  const [search, setSearch] = useState('');
  const [feeRange, setFeeRange] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('OPEN');
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (country !== 'All Countries') params.set('country', country);
    if (search) params.set('search', search);
    if (feeRange) params.set('feeRange', feeRange);
    if (type) params.set('type', type);
    if (status) params.set('status', status);
    params.set('page', page);

    fetch(`${API_BASE}/opportunities?${params.toString()}`)
      .then((r) => r.json())
      .then((json) => setOpportunities(json.success ? json.data : []))
      .finally(() => setLoading(false));
  }, [country, search, feeRange, type, status, page]);

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <Navbar />
      <main className="pt-32 pb-20 max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="mb-10">
          <nav className="flex items-center gap-2 text-[#64748b] text-sm mb-4">
            <Link className="hover:text-[#00255a]" to="/">Home</Link>
            <span className="material-symbols-outlined text-[14px]">chevron_right</span>
            <span className="text-[#00255a] font-bold">Openings</span>
          </nav>
          <h1 className="text-4xl font-bold text-[#00255a]">Study Abroad Scholarships and Opportunities</h1>
          <p className="text-[#64748b] text-lg max-w-2xl mt-4">
            Discover global academic programs and high-tier job placements curated for your career growth.
          </p>
        </div>

        {/* Country Pills */}
        <div className="mb-8 flex items-center gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
          {['All Countries', ...countries.map((item) => item.country)].map((c) => (
            <button
              key={c}
              onClick={() => setCountry(c)}
              className={`flex-shrink-0 px-6 py-3 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${
                country === c
                  ? 'text-white shadow-md'
                  : 'bg-white border border-[#e2e8f0] text-[#64748b] hover:border-[#00255a] hover:text-[#00255a]'
              }`}
              style={country === c ? { background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' } : {}}
            >
              <span>{c}</span>
            </button>
          ))}
        </div>

        {/* Filter Bar */}
        <div className="mb-12 bg-white border border-[#e2e8f0] rounded-2xl p-4 flex flex-col md:flex-row items-center gap-4">
          <div className="w-full md:flex-1 relative">
            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b]">search</span>
            <input
              className="w-full pl-12 pr-4 py-3 bg-[#f7f9fb] border-none rounded-xl focus:ring-2 focus:ring-[#00255a]/10 outline-none"
              placeholder="Search programs or jobs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="w-full md:w-auto flex flex-wrap items-center gap-3">
            <select
              className="bg-[#f7f9fb] border-none rounded-xl py-3 px-4 text-sm cursor-pointer"
              value={feeRange}
              onChange={(e) => setFeeRange(e.target.value)}
            >
              <option value="">Fee Range</option>
              <option value="0-1000">Under $1,000</option>
              <option value="1000-5000">$1,000 - $5,000</option>
              <option value="5000+">$5,000+</option>
            </select>
            <select
              className="bg-[#f7f9fb] border-none rounded-xl py-3 px-4 text-sm cursor-pointer"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="">Program Type</option>
              <option value="STUDY">Study</option>
              <option value="JOB">Job</option>
            </select>
            <div className="flex items-center gap-2 bg-[#f7f9fb] rounded-xl p-1.5 border border-[#e2e8f0]/50">
              <button
                onClick={() => setStatus('OPEN')}
                className={`px-4 py-1.5 rounded-lg text-sm font-semibold ${status === 'OPEN' ? 'bg-white shadow-sm text-[#00255a]' : 'text-[#64748b]'}`}
              >
                Open
              </button>
              <button
                onClick={() => setStatus('CLOSED')}
                className={`px-4 py-1.5 rounded-lg text-sm font-semibold ${status === 'CLOSED' ? 'bg-white shadow-sm text-[#00255a]' : 'text-[#64748b]'}`}
              >
                Closed
              </button>
            </div>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <p className="text-center text-[#64748b] py-20">Loading opportunities...</p>
        ) : opportunities.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#64748b] mb-4">No openings match your filters right now.</p>
            <a
              href="https://wa.me/263775827371"
              className="inline-block text-white px-6 py-3 rounded-lg font-semibold"
              style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
            >
              Contact an Agent
            </a>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {opportunities.map((op) => (
                <OpeningCard key={op.id} opportunity={op} />
              ))}
            </div>
            <div className="mt-20 flex justify-center">
              <button
                onClick={() => setPage(page + 1)}
                className="flex items-center gap-3 bg-white border border-[#e2e8f0] px-8 py-4 rounded-full text-sm font-semibold text-[#00255a] hover:bg-[#f2f4f6] transition-colors shadow-sm"
              >
                <span>Load More Opportunities</span>
                <span className="material-symbols-outlined">expand_more</span>
              </button>
            </div>
          </>
        )}
      </main>
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}

function OpeningCard({ opportunity }) {
  const flag = '🌍';
  const feeLabel = opportunity.type === 'JOB'
    ? (opportunity.salaryFrom ? `From ${opportunity.salaryCurrency} ${opportunity.salaryFrom}` : 'Salary TBD')
    : (opportunity.feeFromAmount ? `From ${opportunity.feeCurrency} ${opportunity.feeFromAmount}` : 'Fee TBD');
  const feeTypeLabel = opportunity.type === 'JOB' ? 'Base Salary' : 'Tuition Fee';

  return (
    <Link to={`/openings/${opportunity.id}`} className="group">
      <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden flex flex-col shadow-[0px_10px_30px_rgba(30,60,114,0.05)] hover:shadow-[0px_20px_40px_rgba(30,60,114,0.12)] hover:-translate-y-1 transition-all">
        <div className="relative h-56">
          <img
            className="w-full h-full object-cover"
            alt={opportunity.country}
            src={opportunity.imageUrl || `/images/countries/${opportunity.country.toLowerCase().replace(/\s+/g, '-')}.jpg`}
          />
          <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-2 shadow-sm">
            <span className="text-sm">{flag}</span>
            <span className="text-xs uppercase text-[#00255a] font-semibold">{opportunity.country}</span>
          </div>
          <div className="absolute top-4 right-4 bg-[#1e3c72]/20 backdrop-blur-sm text-[#00255a] px-3 py-1 rounded-full text-[11px] font-semibold border border-[#1e3c72]/30">
            {opportunity.studyLevel?.toUpperCase() || opportunity.sector?.toUpperCase() || opportunity.type}
          </div>
        </div>
        <div className="p-6 flex-1 flex flex-col">
          <h3 className="text-xl font-bold text-[#00255a] mb-2">{opportunity.title}</h3>
          <p className="text-[#64748b] mb-6">{opportunity.institution?.name || opportunity.country}</p>
          <div className="mt-auto flex items-center justify-between mb-6">
            <div className="flex flex-col">
              <span className="text-[11px] text-[#64748b] uppercase tracking-wider">{feeTypeLabel}</span>
              <span className="text-lg font-bold text-[#365da3]">{feeLabel}</span>
            </div>
            <div className="flex items-center gap-1.5 bg-green-50 text-emerald-700 px-3 py-1 rounded-full border border-green-100">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-xs">{opportunity.status}</span>
            </div>
          </div>
          <button
            className="w-full text-white py-3.5 rounded-xl text-sm font-semibold group-hover:scale-[1.01] transition-all"
            style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
          >
            View Details
          </button>
        </div>
      </div>
    </Link>
  );
}
